import { constVoid, DOM, IO, IOO, pipe, RA, RNEA, RR, s, v } from '@packages/prelude'
import { Autoplay, Swiper, SwiperOptions, Navigation, Thumbs } from 'swiper'

import '../css/main.scss'
import 'bootstrap'

// Trigger download on form success page after 3 seconds.
window.addEventListener('DOMContentLoaded', () => {
  pipe(
    IOO.of({ document }),
    IOO.apFirst(
      pipe(
        window.acfe?.get('acfe_form_success'),
        IOO.fromNullable,
        IOO.chainOptionK<ReadonlyArray<{ name: string }>, { name: string }>(RA.findFirst(form => form.name === 'download-form'))
      )
    ),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector('#download-file')) as IOO.IOOption<HTMLElement>),
    IOO.chainFirstIOK(link =>
      pipe(
        IO.of(
          window.setTimeout(() => {
            link.click()
          }, 3000)
        ),
        IO.chainFirst(timeout =>
          pipe(
            link,
            DOM.addEventListener('click')(() => () => {
              // to avoid multiple downloads we need to cancel the timeout when manually clicking the link.
              window.clearTimeout(timeout)
            })
          )
        )
      )
    )
  )()
})

const makeSwiper = (selector: string, options: SwiperOptions) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector(`#${selector}`)) as IOO.IOOption<HTMLElement>),
    IOO.fold(
      () => constVoid,
      element => IOO.of(new Swiper(element, options))
    )
  )()

const Swipers: RR.ReadonlyRecord<string, SwiperOptions> = {
  reviewsSwiper: {
    direction: 'horizontal',
    modules: [Autoplay, Navigation],
    loop: true,
    autoplay: {
      delay: 5000
    },
    speed: 750,
    slidesPerView: 1,
    navigation: {
      prevEl: '.btn-review-prev',
      nextEl: '.btn-review-next'
    }
  },
  functionsSwiper: {
    direction: 'horizontal',
    modules: [Navigation, Thumbs],
    speed: 750,
    slidesPerView: 1,
    navigation: {
      prevEl: '.btn-function-prev',
      nextEl: '.btn-function-next'
    },
    thumbs: {
      swiper: new Swiper('#functionsThumbs', {
        slidesPerView: 6
      })
    }
  }
}

pipe(
  Swipers,
  RR.foldMapWithIndex(s.Ord)(v.Monoid)((selector, options) => makeSwiper(selector, options))
)

const getTimeFromNumber = (num: number) => {
  let s = Math.floor(num)
  let m = Math.floor(s / 60)
  s -= m * 60
  const h = Math.floor(m / 60)
  m -= h * 60

  return h === 0
    ? `${m}:${String(s % 60).padStart(2, '0')}`
    : `${String(h).padStart(2, '0')}:${m}:${String(s % 60).padStart(2, '0')}`
}

pipe(
  IOO.of({ document }),
  IOO.chain(
    ({ document }) =>
      pipe(document, DOM.querySelectorAll('.audio-block')) as unknown as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>
  ),
  IOO.chainFirstIOK(
    audios => () =>
      pipe(
        audios,
        RNEA.map(audio =>
          pipe(
            IOO.Do,
            IOO.bind('player', () => pipe(audio, DOM.querySelector('audio')) as unknown as IOO.IOOption<HTMLAudioElement>),
            IOO.bind('playBtn', () => pipe(audio, DOM.querySelector('.audio-play'))),
            IOO.bind('timeline', () => pipe(audio, DOM.querySelector('.audio-timeline'))),
            IOO.bind('progress', () => pipe(audio, DOM.querySelector('.audio-progress')) as unknown as IOO.IOOption<HTMLElement>),
            IOO.bind('timeCurrent', () => pipe(audio, DOM.querySelector('.audio-time-current'))),
            IOO.bind('timeLength', () => pipe(audio, DOM.querySelector('.audio-time-length'))),
            IOO.chainFirstIOK(({ timeLength, player }) =>
              pipe(
                player,
                DOM.addEventListener('loadeddata')(() => () => {
                  timeLength.textContent = getTimeFromNumber(player.duration)
                })
              )
            ),
            IOO.chainFirstIOK(({ timeline, player }) =>
              pipe(
                timeline,
                DOM.addEventListener('click')(e => () => {
                  const timelineWidth = window.getComputedStyle(timeline).width
                  const timeToSeek = ((e as MouseEvent).offsetX / parseInt(timelineWidth)) * player.duration
                  player.currentTime = timeToSeek
                })
              )
            ),
            IOO.chainFirstIOK(({ player, playBtn }) =>
              pipe(
                playBtn,
                DOM.addEventListener('click')(() => () => {
                  if (player.paused) {
                    playBtn.innerHTML = '<i class="fas fa-pause fa-xl"></i>'
                    player.play()
                  } else {
                    playBtn.innerHTML = '<i class="fas fa-play fa-xl"></i>'
                    player.pause()
                  }
                })
              )
            ),
            IOO.chainFirstIOK(({ player, playBtn }) =>
              pipe(
                player,
                DOM.addEventListener('ended')(() => () => {
                  playBtn.innerHTML = '<i class="fas fa-play fa-xl"></i>'
                  player.pause()
                })
              )
            ),
            IOO.map(({ player, progress, timeCurrent }) =>
              setInterval(() => {
                progress.style.width = (player.currentTime / player.duration) * 100 + '%'
                timeCurrent.textContent = getTimeFromNumber(player.currentTime)
              }, 500)
            )
          )()
        )
      )
  )
)()
